import type { FtpHistoryEntry } from "../athlete-profile/types/athleteProfile";
import type { DailyHealthRecord } from "../health/dailyHealthTypes";
import type { CompletedActivityRecord } from "../train/completedActivityHistory";
import type { AthleteTimelineEvent } from "../train/athleteTimeline";

export type AthleteHistoryDateRange = {
  start: string | null;
  end: string | null;
};

export type AthleteHistoryCounts = {
  rides: number;
  dailyHealth: number;
};

export type AthleteHistoryAvailableDateRange = {
  rides: AthleteHistoryDateRange;
  dailyHealth: AthleteHistoryDateRange;
  overall: AthleteHistoryDateRange;
};

export type AthleteHistoryIndex = {
  getRidesBetween: (startIso: string, endIso: string) => CompletedActivityRecord[];
  getDailyHealthBetween: (startLocalDate: string, endLocalDate: string) => DailyHealthRecord[];
  getLatestRide: () => CompletedActivityRecord | null;
  getLatestDailyHealth: () => DailyHealthRecord | null;
  getRideCount: () => number;
  getHealthRecordCount: () => number;
  getAvailableDateRange: () => AthleteHistoryAvailableDateRange;
  getRidesInRollingWindowDays: (windowDays: number, anchorIso?: string | null) => CompletedActivityRecord[];
  getDailyHealthInRollingWindowDays: (windowDays: number, anchorLocalDate?: string | null) => DailyHealthRecord[];
  getFtpHistory: () => FtpHistoryEntry[];
  getTimelineEvents: () => AthleteTimelineEvent[];
  getBodyCompositionHistory: () => Array<{ localDate: string; bodyWeightKg: number | null; bodyFatPercentage: number | null; leanBodyMassKg: number | null }>;
  getSourceCoverageSummary: () => {
    Garmin: number;
    TrainingPeaks: number;
    AppleHealth: number;
    Tymewear: number;
  };
};

function toTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function toLocalDateTimestamp(localDate: string | null | undefined): number | null {
  if (!localDate) return null;
  const parsed = Date.parse(`${localDate}T12:00:00.000Z`);
  return Number.isNaN(parsed) ? null : parsed;
}

function sortRidesDescending(records: CompletedActivityRecord[]): CompletedActivityRecord[] {
  return [...records].sort((left, right) => {
    const leftTs = toTimestamp(left.activityDate) ?? 0;
    const rightTs = toTimestamp(right.activityDate) ?? 0;
    return rightTs - leftTs;
  });
}

function sortHealthDescending(records: DailyHealthRecord[]): DailyHealthRecord[] {
  return [...records].sort((left, right) => right.localDate.localeCompare(left.localDate));
}

function rangeFromRides(records: CompletedActivityRecord[]): AthleteHistoryDateRange {
  if (records.length === 0) return { start: null, end: null };
  const sorted = [...records].sort((left, right) => (toTimestamp(left.activityDate) ?? 0) - (toTimestamp(right.activityDate) ?? 0));
  return {
    start: sorted[0]?.activityDate ?? null,
    end: sorted[sorted.length - 1]?.activityDate ?? null
  };
}

function rangeFromHealth(records: DailyHealthRecord[]): AthleteHistoryDateRange {
  if (records.length === 0) return { start: null, end: null };
  const sorted = [...records].sort((left, right) => left.localDate.localeCompare(right.localDate));
  return {
    start: sorted[0]?.localDate ?? null,
    end: sorted[sorted.length - 1]?.localDate ?? null
  };
}

function overallRange(rides: AthleteHistoryDateRange, health: AthleteHistoryDateRange): AthleteHistoryDateRange {
  const candidates = [
    rides.start,
    rides.end,
    health.start ? `${health.start}T00:00:00.000Z` : null,
    health.end ? `${health.end}T23:59:59.999Z` : null
  ].filter((value): value is string => Boolean(value));

  if (candidates.length === 0) {
    return { start: null, end: null };
  }

  const sorted = candidates
    .map(value => ({ iso: value, ts: toTimestamp(value) }))
    .filter((item): item is { iso: string; ts: number } => item.ts !== null)
    .sort((left, right) => left.ts - right.ts);

  if (sorted.length === 0) {
    return { start: null, end: null };
  }

  return {
    start: sorted[0].iso,
    end: sorted[sorted.length - 1].iso
  };
}

export function createAthleteHistoryIndex(input: {
  rides: CompletedActivityRecord[];
  dailyHealth: DailyHealthRecord[];
  ftpHistory: FtpHistoryEntry[];
  timelineEvents: AthleteTimelineEvent[];
}): AthleteHistoryIndex {
  const ridesDesc = sortRidesDescending(input.rides);
  const healthDesc = sortHealthDescending(input.dailyHealth);
  const ftpHistoryDesc = [...input.ftpHistory].sort((left, right) => (toTimestamp(right.effectiveDateIso) ?? 0) - (toTimestamp(left.effectiveDateIso) ?? 0));
  const timelineDesc = [...input.timelineEvents].sort((left, right) => {
    const leftTs = toTimestamp(left.timestamp ?? left.canonicalTimestampIso ?? null) ?? toLocalDateTimestamp(left.eventDate) ?? 0;
    const rightTs = toTimestamp(right.timestamp ?? right.canonicalTimestampIso ?? null) ?? toLocalDateTimestamp(right.eventDate) ?? 0;
    return rightTs - leftTs;
  });

  const rideRange = rangeFromRides(ridesDesc);
  const healthRange = rangeFromHealth(healthDesc);

  const getRidesBetween = (startIso: string, endIso: string): CompletedActivityRecord[] => {
    const start = toTimestamp(startIso);
    const end = toTimestamp(endIso);
    if (start === null || end === null) return [];
    const lower = Math.min(start, end);
    const upper = Math.max(start, end);
    return ridesDesc.filter(record => {
      const ts = toTimestamp(record.activityDate);
      return ts !== null && ts >= lower && ts <= upper;
    });
  };

  const getDailyHealthBetween = (startLocalDate: string, endLocalDate: string): DailyHealthRecord[] => {
    const lower = startLocalDate <= endLocalDate ? startLocalDate : endLocalDate;
    const upper = startLocalDate <= endLocalDate ? endLocalDate : startLocalDate;
    return healthDesc.filter(record => record.localDate >= lower && record.localDate <= upper);
  };

  const getRidesInRollingWindowDays = (windowDays: number, anchorIso: string | null = null): CompletedActivityRecord[] => {
    if (windowDays <= 0 || ridesDesc.length === 0) return [];
    const anchor = toTimestamp(anchorIso) ?? toTimestamp(ridesDesc[0].activityDate) ?? Date.now();
    const cutoff = anchor - (windowDays * 24 * 60 * 60 * 1000);
    return ridesDesc.filter(record => {
      const ts = toTimestamp(record.activityDate);
      return ts !== null && ts >= cutoff && ts <= anchor;
    });
  };

  const getDailyHealthInRollingWindowDays = (windowDays: number, anchorLocalDate: string | null = null): DailyHealthRecord[] => {
    if (windowDays <= 0 || healthDesc.length === 0) return [];
    const anchorDate = anchorLocalDate ?? healthDesc[0].localDate;
    const anchorTs = toLocalDateTimestamp(anchorDate) ?? Date.now();
    const cutoff = anchorTs - (windowDays * 24 * 60 * 60 * 1000);
    return healthDesc.filter(record => {
      const ts = toLocalDateTimestamp(record.localDate);
      return ts !== null && ts >= cutoff && ts <= anchorTs;
    });
  };

  const getSourceCoverageSummary = () => {
    let Garmin = 0;
    let TrainingPeaks = 0;
    let Tymewear = 0;

    for (const ride of ridesDesc) {
      const sources = ride.sourceProvenance ?? [];
      if (sources.length === 0) {
        const sourceType = ride.importMetadata?.sourceType ?? "";
        if (/garmin/i.test(sourceType)) Garmin += 1;
        if (/trainingpeaks/i.test(sourceType)) TrainingPeaks += 1;
        if (/tymewear/i.test(sourceType)) Tymewear += 1;
        continue;
      }

      const hasGarmin = sources.some(source => /garmin/i.test(source.sourceType));
      const hasTrainingPeaks = sources.some(source => /trainingpeaks/i.test(source.sourceType));
      const hasTymewear = sources.some(source => /tymewear/i.test(source.sourceType));
      if (hasGarmin) Garmin += 1;
      if (hasTrainingPeaks) TrainingPeaks += 1;
      if (hasTymewear) Tymewear += 1;
    }

    return {
      Garmin,
      TrainingPeaks,
      AppleHealth: healthDesc.length,
      Tymewear
    };
  };

  return {
    getRidesBetween,
    getDailyHealthBetween,
    getLatestRide: () => ridesDesc[0] ?? null,
    getLatestDailyHealth: () => healthDesc[0] ?? null,
    getRideCount: () => ridesDesc.length,
    getHealthRecordCount: () => healthDesc.length,
    getAvailableDateRange: () => ({
      rides: rideRange,
      dailyHealth: healthRange,
      overall: overallRange(rideRange, healthRange)
    }),
    getRidesInRollingWindowDays,
    getDailyHealthInRollingWindowDays,
    getFtpHistory: () => ftpHistoryDesc,
    getTimelineEvents: () => timelineDesc,
    getBodyCompositionHistory: () => healthDesc.map(record => ({
      localDate: record.localDate,
      bodyWeightKg: record.bodyComposition.bodyWeightKg,
      bodyFatPercentage: record.bodyComposition.bodyFatPercentage,
      leanBodyMassKg: record.bodyComposition.leanBodyMassKg
    })),
    getSourceCoverageSummary
  };
}
